/* eslint-disable no-param-reassign, no-unused-expressions */
import { DocItem, DocStatus } from '@/pages/document/Types';
import { throttle } from 'lodash';
import SparkMD5 from 'spark-md5';
import { uploadFile } from '@/pages/document/server/documentServer';
import i18next from 'i18next';
import Message from '@/component/Message';

interface DocChangeData{
  status?: DocStatus;
  title?: string;
  content?: string;
  isModify?: boolean;
}
// 根据id修改对应文档内容
export function updateDocById(origin: DocItem[], id: number, data: DocChangeData = {}) {
  function loop(originData: DocItem[]) {
    originData.forEach((item: DocItem, key: number) => {
      if (item.docId === id) {
        originData[key] = { ...item, ...data };
      }
      if (item.childDocs && item.childDocs.length > 0) {
        loop(item.childDocs);
      }
    });
  }
  loop(origin);

  return origin;
}

/**
 * level: 1-1-2 标识当前节点的位置
 * isChild: 表示添加的是子节点还是同级节点
 */
export function getParentDocId(origin: DocItem[], level: string, isChild: boolean) {
  let parentId = 0;
  let index = 0;
  const levelArr = level.split('-');
  const { length } = levelArr;
  const firstIndex = levelArr[length - 1];
  // 直接点击新建按钮
  if (firstIndex === 'new') {
    parentId = 0;
    index = origin.length;
    return { parentId, index };
  }
  // 点击文档创建
  if (isChild) {
    const data = levelArr.reduce((pre: any, next: any) => (Array.isArray(pre) ? pre[next] : pre.childDocs[next]), origin);
    parentId = data.docId;
    index = data.childDocs.length;
  } else {
    const newArr: number[] = levelArr.map((item) => parseInt(item, 10)).slice(0, -1);
    const data = newArr.reduce((pre: any, next: number) => (Array.isArray(pre) ? pre[next] : pre.childDocs[next]), origin);
    parentId = data.docId || 0;
    index = parseInt(firstIndex, 10) + 1;
  }

  return { parentId, index };
}

// 文档树节点添加 Expand
export function changeDocTree(tree: DocItem[], isExpand = false) {
  function loop(data: DocItem[]) {
    return data.map((item: DocItem) => {
      const origin: DocItem = { ...item, isExpand };
      if (origin.childDocs && origin.childDocs.length) {
        origin.childDocs = loop(origin.childDocs);
      }
      return origin;
    });
  }

  return loop(tree);
}

// 展开 或者 关闭 结构树
export function isExpandTree(tree: DocItem[], level: string, isExpand: boolean) {
  const arr = level.split('-');
  const newTree = tree;
  if (tree && tree.length === 0) {
    return newTree;
  }
  if (arr.length === 1 && arr[0] === 'new') {
    return newTree;
  }

  try {
    if (!isExpand) {
      arr.reduce((origin: any, key) => {
        origin[key].isExpand = true;
        return origin[key].childDocs;
      }, newTree);
    } else {
      const list = arr.reduce((origin: any, key, i) => (i < arr.length - 1 ? origin[key].childDocs : origin[key]), newTree);
      list.isExpand = false;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }

  return newTree;
}

export function treeForeach(tree: DocItem[], callback: Function, flag = '') {
  tree.forEach((data: DocItem, key: number) => {
    const index = `${flag ? `${flag}-` : ''}${key}`;
    callback(tree, data, index);
    if (data.childDocs) {
      treeForeach(data.childDocs, callback, index);
    }
  });
}

// 互斥打开
export function openTreeExpand(tree: DocItem[], level: string, isExpand: boolean) {
  const arr = level.split('-');
  if (tree && tree.length === 0) {
    return tree;
  }

  tree = changeDocTree(tree);

  try {
    arr.reduce((origin: any, key) => {
      origin[key].isExpand = true;
      return origin[key].childDocs;
    }, tree);

    treeForeach(tree, (parent: DocItem[], child: DocItem, index: string) => {
      if (index === level) {
        child.isExpand = !isExpand;
      }
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }

  return tree;
}

// 根据 id 获取结构 层级
export function getNodeLevel(tree: DocItem[], id: number) {
  let level = '';
  function loop(data: DocItem[], idx = '') {
    return data.forEach((item: DocItem, key: number) => {
      const index = idx ? `${idx}-${key}` : `${key}`;
      if (Number(item.docId) === Number(id)) {
        level = index;
      }
      if (item.childDocs && item.childDocs.length) {
        loop(item.childDocs, index);
      }
    });
  }
  loop(tree);

  return level;
}

export function getNodeIdLevel(tree: DocItem[], id: number) {
  let level = '';
  function loop(data: any, idx = '') {
    return data.forEach((item: any) => {
      const index = idx ? `${idx}-${item.docId}` : `${item.docId}`;
      if (item.docId === id) {
        level = index;
      }
      if (item.childDocs && item.childDocs.length) {
        loop(item.childDocs, index);
      }
    });
  }
  loop(tree);

  return level;
}

// 递归遍历树结构
export function treeLoop(tree: DocItem[], callback: any, parentId = 0) {
  tree.forEach((data: DocItem, key: number) => {
    callback(tree, data, key, parentId);
    if (data.childDocs) {
      treeLoop(data.childDocs, callback, data.docId);
    }
  });
}

// 根据id 展开相应的节点
export function isExpandTreeById(list: DocItem[], docId: number) {
  treeLoop(list, (tree: DocItem[], data: DocItem) => {
    data.isExpand = data.docId === docId;
  });
  return list;
}

// 根据 id 删除 对应的节点  并获取相关性的节点
export function deleteDocById(list: DocItem[], docId: number, currentDocId?: number) {
  let id = 0;
  treeLoop(list, (parent: DocItem[], data: DocItem, index: number, parentId: number) => {
    if (data.docId === docId) {
      parent.splice(index, 1);
      // 没有子元素
      if (parent.length === 0) {
        id = parentId;
        return;
      }
      // 当前删除的文档就是活跃文档
      if (currentDocId === docId) {
        const key = Math.min(parent.length - 1, index);
        id = parent[key].docId;
        return;
      }
      // 当前删除的文档是活跃文档的父文档
      if (isChildNode(data.childDocs || [], currentDocId)) {
        id = parent[0]?.docId || 0;
        return;
      }
      id = currentDocId || 0;
    }
  });
  return { list, id };
}

// 根据id 判断是是子节点
export function isChildNode(list: DocItem[], docId?: number) {
  let is = false;
  treeLoop(list, (parent: DocItem[], data: DocItem) => {
    if (data.docId === docId) {
      is = true;
    }
  });
  return is;
}

// 根据 level 新增 对应的接点
export function addDocByLevel(list: any, level: string, isChild: boolean, data: any) {
  let index = 0;
  const levelArr = level.split('-');
  const { length } = levelArr;
  const firstIndex = levelArr[length - 1];
  // 直接点击新建按钮
  if (firstIndex === 'new') {
    index = list.length;
    list.splice(index, 0, { ...data });
    return list;
  }
  // 点击文档创建
  if (isChild) {
    const origin = levelArr.reduce((pre: any, next: any) => (Array.isArray(pre) ? pre[next] : pre.childDocs[next]), list);
    index = origin.childDocs.length;
    origin.childDocs.splice(index, 0, { ...data });
    return list;
  }
  const newArr: number[] = levelArr.map((item) => parseInt(item, 10)).slice(0, -1);
  const origin = newArr.reduce((pre: any, next: number) => (Array.isArray(pre) ? pre[next] : pre.childDocs[next]), list);
  index = parseInt(firstIndex, 10) + 1;
  if (Array.isArray(origin)) {
    origin.splice(index, 0, { ...data });
  } else {
    origin.childDocs.splice(index, 0, { ...data });
  }

  return list;
}

// 文档树数据 添加 level 和 key
export function transformDocTree(list: DocItem[]) {
  function loop(data: DocItem[], level = 1, key = '0'): any {
    return data.map((item: DocItem, index: number) => {
      if (item.childDocs && item.childDocs.length) {
        const childArr = item.childDocs.map((child: DocItem, idx: number) => ({
          ...child,
          level: level + 1,
          key: `${key}-${index}-${idx}`,
          children: loop(child?.childDocs || [], level + 1, `${key}-${index}-${idx}`),
        }));
        return {
          ...item, children: childArr, level, key: `${key}-${index}`,
        };
      }
      return {
        ...item, children: [], level, key: `${key}-${index}`,
      };
    });
  }
  return loop(list);
}

// 将文档树转化为  map 表
export function transfromTreetoMap(list: any) {
  const mapList: any = { 0: {} };
  const levelOne: number[] = [];
  function loop(data: any) {
    if (data && data.length) {
      return data.map((item: any) => {
        mapList[item.docId] = { parentId: item.parentId, status: item.statusMap, childIds: loop(item.children) };
        return item.docId;
      });
    }
    return [];
  }
  list.forEach((item: any) => {
    levelOne.push(item.docId);
    mapList[item.docId] = { parentId: item.parentId, status: item.statusMap, childIds: loop(item.children) };
  });

  mapList[0] = { childIds: levelOne, parentId: -1, status: 1 };

  return mapList;
}

// 根据页面 h 标签获取导航
export function getHeaderList(Container: HTMLDivElement, callBack?: Function) {
  const AuthorItems: any = Array.from(Container.querySelectorAll('h1, h2, h3, h4, h5, h6'));
  const { length } = AuthorItems;
  // 给容器里面所有的 h1 - h5 标签添加 a 标签;
  let fistLevel = 1;
  const titleList: string[] = [];

  const listString = AuthorItems.map((el: any, key: number) => {
    const level = el.nodeName[1];
    const title = el.textContent || el.innerText;
    let titleHash = title;

    // title 名字重复 重新设置hash
    if (titleList.includes(title)) {
      titleHash = `${title}-${titleList.filter((item) => item === title).length}`;
    }
    titleList.push(title);

    const id = `item-link-${titleHash}`;
    const href = `#${id}`.replace(/"/g, '&quot;');
    if (key === 0) {
      fistLevel = parseInt(level, 10);
    }
    el.id = id;
    const showLevel = (level - fistLevel) > 0 ? (level - fistLevel) : 0;
    // 需要添加 a 标签
    // if (needAddAtarget === true) {
    //   const a = document.createElement('a');
    //   a.href = href;
    //   el.appendChild(a);
    // }
    return `<div 
    class="author-link-item author-${id}" 
    style="padding-left: ${showLevel * 12}px;"
    ><a href="${href}" title="${title}" class="a-${id}">${title}</a></div>`;
  });

  // 监听容器的滚动
  if (Container && length) {
    const { top, height } = Container.getBoundingClientRect();
    let boxIsScroll = false; // 判断父容器是否可以滚动
    let scrollDirection = true; // 判断父元素滚动方向

    Container.addEventListener('scroll', throttle(() => {
      // 如果是点击icon的话 我们就禁止滚动的判断
      if ((window as any).isClickAuthor) return;

      const box: any = document.getElementById('author-list-content');
      const sign: any = document.getElementById('author-sign-icon');
      let boxContainer = box && box.parentNode;
      let scorllStart = Container.scrollTop;

      // 判断是否滚动到底部 是否滚动到顶部
      // const bottom = Container.scrollHeight - Container.scrollTop === Container.clientHeight;
      // const totop = Container.scrollTop === 0;

      if (box) {
        const active = box.getElementsByClassName('active');
        boxContainer = boxContainer || box.parentNode;
        // 判断父容器是否可以滚动
        boxIsScroll = boxContainer.scrollHeight > boxContainer.clientHeight;
        // 判断父元素滚动方向
        const scrollEnd = Container.scrollTop;
        scrollDirection = scrollEnd > scorllStart; // true 向下滚动  false 向上滚动
        scorllStart = scrollEnd;

        // 滚动时计算 位置
        for (let i = 0; i < length; i++) {
          const { top: childTop, height: childHeight } = AuthorItems[i].getBoundingClientRect();
          const position = childTop + childHeight - top;

          if (height > position && position > 0) {
            if (active.length) {
              active[0].classList.remove('active');
            }
            // 如果滚动到底部的话 就直接激活最后一个元素
            // const idx = bottom ? length - 1 : i;
            const styletop = (i * 28);
            sign.style.top = `${styletop}px`;
            box.getElementsByClassName('author-link-item')[i]?.classList.add('active');

            // 判断author容器是否可以滚动
            if (boxIsScroll && scrollDirection && styletop > boxContainer.clientHeight / 2) { // 向下滚动
              boxContainer.scrollTo({
                top: styletop - boxContainer.clientHeight / 2 - 28,
                left: 0,
                // behavior: 'smooth',
              });
            }

            if (boxIsScroll && !scrollDirection && box.clientHeight - styletop > boxContainer.clientHeight / 2) { // 向上滚动
              boxContainer.scrollTo({
                top: styletop - boxContainer.clientHeight / 2 + 28,
                left: 0,
                // behavior: 'smooth',
              });
            }

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            callBack && callBack(`#item-link-${AuthorItems[i].textContent}`);
            return;
          }
        }

        // 如果第一个元素的 顶部距离大于 文档的高度 取消活跃
        if (length) {
          const { top: childTop } = AuthorItems[0].getBoundingClientRect();
          if (childTop > height) {
            if (active.length) {
              active[0].classList.remove('active');
            }
            sign.style.top = '-22px';

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            callBack && callBack('');
          }
        }
      }
    }, 30));
  }

  return listString.length ? `<div class="author-inner" id="author-list-content">
    <div class="author-line"><span id="author-sign-icon"></span></div>
    <div class="author-link">${listString.join('')}</div>
  </div` : '';
}

// 文件处理
export function fileUpload(file: any, uploadType = 'IMAGE', obj = {}) {
  return new Promise((resolve, reject) => {
    try {
      const formData = new FormData();
      formData.append('upload_type', uploadType);
      formData.append('file', file);

      const fileReader = new FileReader();
      const spark = new SparkMD5(); // 创建md5对象（基于SparkMD5）
      fileReader.readAsBinaryString(file);

      // if (dataFile.size > 1024 * 1024 * 10) {
      //   const data1 = dataFile.slice(0, 1024 * 1024 * 10); // 将文件进行分块 file.slice(start,length)
      //   fileReader.readAsBinaryString(data1); // 将文件读取为二进制码
      // } else {
      //   fileReader.readAsBinaryString(dataFile);
      // }

      // 文件读取完毕之后的处理
      fileReader.onload = async function load(e: any) {
        spark.appendBinary(e.target.result);
        const md5 = spark.end();
        formData.append('file_signature', md5);
        try {
          const result: any = await uploadFile(formData, obj);
          if (uploadType === 'IMAGE') {
            resolve(result.data.data.url);
          } else {
            // if (result.code !== errCode.SUCCESS) {
            //   Message.error(i18next.t('video_upload_error'));
            // }
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.src = URL.createObjectURL(file);
            video.onloadedmetadata = () => {
              resolve({ src: result.data.data.url, height: video.videoHeight, width: video.videoWidth });
            };
          }
        } catch (err) {
          Message.error(i18next.t('video_upload_error'));
          resolve(err);
        }
      };
    } catch (err) {
      reject(err);
    }
  });
}

export function getDocId(id: string) {
  if (!id) {
    return 0;
  }
  try {
    return parseInt(id, 10);
  } catch (e) {
    return 0;
  }
}

interface ScrollTopOption {
  container: Element;
  distance: number;
  duration?: number;
  handler?: () => void;
}
export function scrollTo(option: ScrollTopOption) {
  const { container = document.scrollingElement, distance } = option;
  // cancel if already on top
  if ((container as Element).scrollTop === distance) return;
  const currentScrollTop = (container as Element).scrollTop;
  let scrollY = currentScrollTop;
  const stepDistance = currentScrollTop - distance;
  const long = Math.abs(stepDistance) / 10;

  function step() {
    if (stepDistance > 0) {
      scrollY -= long;
      if (scrollY <= distance) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        option.handler && option.handler();
        (container as Element).scrollTop = distance;
        return;
      }
    } else {
      scrollY += long;
      if (scrollY >= distance) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        option.handler && option.handler();
        (container as Element).scrollTop = distance;
        return;
      }
    }
    (container as Element).scrollTop = scrollY;
    window.requestAnimationFrame(step);
  }

  window.requestAnimationFrame(step);
}

export function hasId(list: any, id: number) {
  let flag = false;
  function loop(data: any) {
    data.forEach((item: any) => {
      if (item.docId === id) {
        flag = true;
      }
      if (item.childDocs && item.childDocs.length) {
        loop(item.childDocs);
      }
    });
  }
  loop(list);

  return flag;
}

export function searchDocList(tree: DocItem[], value: string) {
  function loop(list: DocItem[]): DocItem[] {
    return list.filter((item: DocItem) => {
      const flag = item.title.includes(value);
      item.isExpand = true;
      item.title = item.title.replace(value, ($1) => `<b>${$1}</b>`);
      item.childDocs = loop(item.childDocs || []);
      return flag || item.childDocs.length > 0;
    });
  }

  return loop(tree);
}
